import Raffle from '../models/Raffle';
import {
	CLEAR_FETCHED_RAFFLE,
	FETCHED_RAFFLE,
	FETCHING_RAFFLE,
	FETCHING_CONSENTS,
	FETCHED_CONSENTS,
	FETCHING_COUNTRIES,
	FETCHED_COUNTRIES,
	FETCHING_ROMANIA_COUNTIES_AND_CITIES,
	FETCHED_ROMANIA_COUNTIES_AND_CITIES,
	UPDATE_ROMANIAN_CITY_OPTIONS,
	UPDATE_ADDRESS_COMPONENTS
} from '../actions/RaffleFormActions';
import GenderOptions from '../enums/GenderOptions';
import AccountOptions from '../enums/AccountOptions';
import RegistrationStep from '../enums/RegistrationStep';
import RegistrationFormReducer from './registrationForm/RegistrationFormReducer';
import ConsentModalReducer from './registrationForm/ConsentModalReducer';
import reduceReducers from 'reduce-reducers';
import TextInput from '../components/form/TextInput';
import Validator from '../components/form/Validators';
import produce from 'immer';
import CustomerStep from '../components/blocks/raffleForm/CustomerStep';

export default class RaffleFormReducer {

	/**
	 * @param {object} state
	 * @param {object} action
	 */
	static reduce(state = initialState, action) {
		// Use helper reducers to split some action handling into multiple classes
		state = RaffleFormReducer.reduceChainedReducers(state, action);
		switch (action.type) {
			case FETCHING_RAFFLE:
				return produce(state, draftState => {
					draftState.fetchingRaffle = true;
				});
			case CLEAR_FETCHED_RAFFLE:
				return produce(state, draftState => {
					draftState.raffle = null;
					draftState.raffleNotFound = false;
					draftState.fetchingRaffle = false;
				});
			case FETCHED_RAFFLE:
				return RaffleFormReducer.normalize(
					produce(state, draftState => {
						draftState.raffle = action.raffle;
						draftState.raffleNotFound = action.raffle === null;
						draftState.fetchingRaffle = false;
					})
				);
			case FETCHING_CONSENTS:
				return produce(state, draftState => {
					draftState.fetchingConsents = true;
				});
			case FETCHED_CONSENTS:
				return produce(state, draftState => {
					draftState.consents = action.consents;
					draftState.fetchingConsents = false;
					draftState.fields.consents = RaffleFormReducer.generateDefaultConsentValues(action.consents);
				});
			case FETCHING_COUNTRIES:
				return produce(state, draftState => {
					draftState.fetchingCountries = true;
				});
			case FETCHED_COUNTRIES:
				return produce(state, draftState => {
					draftState.countries = action.countries;
					draftState.fetchingCountries = false;
					draftState.fields.country.options = action.countries;
					draftState.fields.usState.options = action.usStates;
					draftState.fields.caState.options = action.caStates;
				});
			case FETCHING_ROMANIA_COUNTIES_AND_CITIES:
				return produce(state, draftState => {
					draftState.fetchingRomanianCountiesAndCities = true;
				});
			case FETCHED_ROMANIA_COUNTIES_AND_CITIES:
				return produce(state, draftState => {
					draftState.romanianCountiesAndCities = action.romanianCountiesAndCities;
					draftState.fetchingRomanianCountiesAndCities = false;
					draftState.fields.romanianCounty.options = RaffleFormReducer.buildRomanianCountyOptions(action.romanianCountiesAndCities);
					draftState.fields.romanianCity.options = [];
				});
			case UPDATE_ROMANIAN_CITY_OPTIONS:
				return produce(state, draftState => {
					const value = state.fields.romanianCounty.value;
					if (value) {
						const countyWithCities = state.romanianCountiesAndCities.find((countyWithCities) => countyWithCities.isoCode === value);
						draftState.fields.romanianCity.options = RaffleFormReducer.buildRomanianCityOptions(countyWithCities.cities);
					}
				});
			case UPDATE_ADDRESS_COMPONENTS:
				return produce(state, draftState => {
					const fields = action.fields;
					draftState.fields.city = RaffleFormReducer.updateTextInputState(draftState.fields.city, fields.city, CustomerStep.inputs.city.validators);
					draftState.fields.streetName = RaffleFormReducer.updateTextInputState(draftState.fields.streetName, fields.streetName, CustomerStep.inputs.streetName.validators);
					let newHouseNumber = '';
					if ((fields.streetNumber && fields.originalAddress.includes(fields.streetNumber)) ||
						(fields.premise && fields.originalAddress.includes(fields.premise))
					) {
						newHouseNumber = fields.houseNumber;
					}
					if (newHouseNumber === '') {
						const matches = fields.suggestionMainText.match(/\d+/g);
						if (matches) {
							const numbers = matches.map(Number);
							if (numbers.length > 0) {
								newHouseNumber = numbers[0];
							}
						}
					}
					draftState.fields.houseNumber = RaffleFormReducer.updateTextInputState(draftState.fields.houseNumber, newHouseNumber, CustomerStep.inputs.houseNumber.validators);
					draftState.fields.postalCode = RaffleFormReducer.updateTextInputState(draftState.fields.postalCode, fields.postalCode, CustomerStep.inputs.postalCode.validators);

					// Set Country if it is not yet set
					if (!draftState.fields.country.value) {
						// This ensures that all related state properties get updated as well - for example, the deliveryService
						draftState = RegistrationFormReducer.updateCountry(draftState, fields.country);
					}

					draftState.fields.usState.value = fields.usState;
					draftState.fields.usState.touched = true;
					draftState.fields.caState.value = fields.caState;
					draftState.fields.caState.touched = true;
				});
			default:
				return state;
		}
	}

	/**
	 * @param {Object} state
	 * @param {string} value
	 * @param {null|Validator[]} validators
	 *
	 * @return {Object}
	 */
	static updateTextInputState(state, value, validators = []) {
		return produce(state, draftState => {
			draftState.value = value;
			draftState.touched = value !== '';
			if (validators) {
				const { valid, errors } = Validator.validateAll(value, validators);
				draftState.valid = valid;
				draftState.errors = errors;
			}
			else {
				draftState.valid = true;
				draftState.errors = [];
			}
		});
	}

	/**
	 * @param {string[]} cities
	 * @return {SelectOption[]}
	 */
	static buildRomanianCityOptions(cities) {
		return cities.map(city => ({ value: city, label: city }));
	}

	/**
	 * @param {Object} countiesAndCities
	 * @return {SelectOption[]}
	 */
	static buildRomanianCountyOptions(countiesAndCities) {
		return countiesAndCities.map(countyWithCities => ({ value: countyWithCities.isoCode, label: countyWithCities.name }));
	}

	/**
	 * @return Object
	 */
	static generateDefaultConsentValues(consents) {
		const defaultConsentValues = {};
		consents.forEach(consent => {
			defaultConsentValues[consent.consentVersionId] = { value: false, touched: false };
		});
		return defaultConsentValues;
	}

	/**
	 * @return Object
	 */
	static createDefaultAccordionState() {
		return {
			contactAccordion: {
				isOpen: true,
				isVisible: true,
				isChecked: false,
				appear: false,
				wasValidated: false
			},
			aboutYouAccordion: {
				isOpen: false,
				isVisible: false,
				isChecked: false,
				appear: true,
				wasValidated: false
			},
			deliveryAddressAccordion: {
				isOpen: false,
				isVisible: false,
				isChecked: false,
				appear: true,
				wasValidated: false
			}
		};
	}

	/**
	 * @return Object
	 */
	static createDefaultFieldsState() {
		return {
			account: { value: AccountOptions.NEW_CUSTOMER.value, touched: false },
			email: TextInput.getDefaultValues(),
			phone: TextInput.getDefaultValues(),
			gender: { value: GenderOptions.MR.value, touched: false },
			firstName: TextInput.getDefaultValues(),
			lastName: TextInput.getDefaultValues(),
			instagramUsername: TextInput.getDefaultValues(),
			birthday: {},
			streetName: TextInput.getDefaultValues(),
			houseNumber: TextInput.getDefaultValues(),
			additional: TextInput.getDefaultValues(),
			postalCode: TextInput.getDefaultValues(),
			city: TextInput.getDefaultValues(),
			country: { value: '', touched: false },
			usState: { value: '', touched: false },
			caState: { value: '', touched: false },
			romanianCounty: { value: '', touched: false, /** @type ?SelectOption[] */ options: null },
			romanianCity: { value: '', touched: false, /** @type ?SelectOption[] */ options: null },
			consents: {}
		};
	}

	/**
	 * @param {object} state
	 * @return {*}
	 */
	static normalize = (state) => ({
		...state,
		raffle: state.raffle === null ? null : Raffle.normalize(state.raffle)
	});
}

RaffleFormReducer.reduceChainedReducers = reduceReducers(
	RegistrationFormReducer.reduce,
	ConsentModalReducer.reduce,
);

/**
 * @typedef {Object} RegistrationFormFields
 * @property {SelectData} account
 * @property {TextInputData} email
 * @property {TextInputData} phone
 * @property {SelectData} gender
 * @property {TextInputData} firstName
 * @property {TextInputData} lastName
 * @property {SelectData} birthday
 * @property {TextInputData} streetName
 * @property {TextInputData} houseNumber
 * @property {TextInputData} additional
 * @property {TextInputData} postalCode
 * @property {SelectData} city
 * @property {SelectData} country
 * @property {Object} consents
 */

const initialState = {
	raffle: null,
	consents: null,
	countries: null,
	romanianCountiesAndCities: null,
	raffleNotFound: false,
	fetchingRaffle: false,
	fetchingConsents: false,
	fetchingCountries: false,
	fetchingRomanianCountiesAndCities: false,
	deliveryService: null,
	fields: RaffleFormReducer.createDefaultFieldsState(),
	accordions: RaffleFormReducer.createDefaultAccordionState(),
	submittingRegistration: false,
	createdRaffleInfo: { id: null },

	/** @type RegistrationStep */
	activeStep: RegistrationStep.CUSTOMER,

	/** @type string[] */
	checkedSteps: [],

	/** @type string[] */
	errors: [],

	/** @type boolean */
	checkingContactDuplicity: false,

	consentModal: {
		show: false,
		title: '',
		detailUrl: null
	},

	consentDetails: {},
	fetchingConsentDetail: false,
	registration: null
};



// WEBPACK FOOTER //
// ./reducers/RaffleFormReducer.js